import EmptyState from "../../../../../shared/components/EmptyState";
import Spinner from "../../../../../shared/components/Spinner";
import UserAvatar from "../../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import type {
  OpenMatchItem,
  OpenMatchParticipantStatus,
} from "../../../../matches/shared/types/matches";
import {
  formatOpenMatchCategories,
  formatOpenMatchScheduleRange,
  resolveOpenMatchAvailableSlots,
  resolveOpenMatchDisplayTitle,
} from "../../../../matches/shared/utils/openMatchPresentation";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileOpenMatchGroup = {
  key: string;
  title: string;
  matches: OpenMatchItem[];
};

type ProfileOpenMatchesTabProps = {
  open: boolean;
  onToggle: () => void;
  groups: ProfileOpenMatchGroup[];
  total: number;
  loading: boolean;
  error: string;
  userId?: number | null;
  onEditMatch: (match: OpenMatchItem) => void;
  onReviewParticipant: (
    matchId: number,
    participantId: number,
    status: OpenMatchParticipantStatus,
  ) => void;
  reviewingParticipantId?: number | null;
};

export default function ProfileOpenMatchesTab({
  open,
  onToggle,
  groups,
  total,
  loading,
  error,
  userId,
  onEditMatch,
  onReviewParticipant,
  reviewingParticipantId = null,
}: ProfileOpenMatchesTabProps) {
  const matchTexts = siteTexts.openMatches.detail;
  const visibleGroups = groups.filter((group) => group.matches.length > 0);

  return (
    <ProfileAccordionSection
      id="profile-open-matches"
      icon="group_add"
      title={siteTexts.profile.openMatches.title}
      subtitle={siteTexts.profile.openMatches.subtitle}
      open={open}
      onToggle={onToggle}
    >
      <div className="user-profile-content-section profile-open-matches-content">
        <UserProfileSectionHeader
          className="profile-open-matches-header"
          icon="group_add"
          title={siteTexts.profile.openMatches.title}
          description={siteTexts.profile.openMatches.subtitle}
        />
        {loading && (
          <p className="status-message">
            <Spinner label={siteTexts.common.loading} />
          </p>
        )}
        {error && <p className="status-message">{error}</p>}
        {!loading && !error && total === 0 && (
          <EmptyState
            className="profile-empty-state"
            icon={<span className="material-symbols-outlined">group_add</span>}
            title={siteTexts.profile.openMatches.empty}
            description={siteTexts.profile.openMatches.emptyHint}
          />
        )}
        {!loading && !error && total > 0 && (
          <div className="profile-open-match-groups">
            {visibleGroups.map((group) => (
              <section
                key={`profile-open-match-group-${group.key}`}
                className="profile-open-match-category"
              >
                <div className="profile-open-match-category-header">
                  <h3>{group.title}</h3>
                </div>
                <div className="profile-my-games-list">
                  {group.matches.map((match) => {
                      const isOrganizer = userId === match.organizer.id;
                      const pendingParticipants =
                        isOrganizer && match.status !== "canceled"
                        ? (match.participants || []).filter((participant) =>
                            ["pending", "invited"].includes(
                              participant.status,
                            ),
                          )
                      : [];
                    return (
                      <article
                        key={`profile-open-match-${group.key}-${match.id}`}
                        className="profile-my-game-card profile-open-match-card"
                      >
                        <div className="profile-my-game-main">
                          <span className="profile-my-game-kicker">
                            {formatOpenMatchCategories(match.categories) ||
                              siteTexts.profile.myGames.categoryFallback}
                          </span>
                          <h3>{resolveOpenMatchDisplayTitle(match)}</h3>
                          <strong>
                            {match.clubName ||
                              siteTexts.profile.openMatches.clubFallback}
                          </strong>
                        </div>
                        <div className="profile-my-game-schedule">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            event
                          </span>
                          <div>
                            <strong>
                              {formatOpenMatchScheduleRange(match)}
                            </strong>
                            <small>
                              {resolveOpenMatchAvailableSlots(match)}{" "}
                              {siteTexts.profile.openMatches.slotsSuffix}
                            </small>
                          </div>
                        </div>
                        <div className="profile-open-match-actions">
                          <a
                            className="profile-my-game-detail-link"
                            href={`#/partidas/${match.id}`}
                          >
                            {siteTexts.profile.openMatches.view}
                          </a>
                          {isOrganizer && match.status !== "canceled" ? (
                            <button
                              type="button"
                              className="profile-my-game-detail-link profile-my-game-detail-link--secondary"
                              onClick={() => onEditMatch(match)}
                            >
                              {siteTexts.profile.openMatches.edit}
                            </button>
                          ) : null}
                        </div>
                        {pendingParticipants.length > 0 ? (
                          <div className="profile-open-match-requests">
                            <strong>
                              {siteTexts.profile.openMatches.requestsTitle}
                            </strong>
                            {pendingParticipants.map((participant) => {
                                const canConfirm =
                                  participant.status === "pending";
                              return (
                                <div
                                  key={`profile-open-match-participant-${participant.id}`}
                                  className="profile-open-match-request"
                                >
                                  <UserAvatar
                                    name={
                                      participant.participantName ||
                                      matchTexts.participantFallback
                                    }
                                    avatarUrl={
                                      participant.avatarUrl || undefined
                                    }
                                    sex={participant.sex || undefined}
                                    size={38}
                                  />
                                  <div>
                                    <strong>
                                      {participant.participantName ||
                                        matchTexts.participantFallback}
                                    </strong>
                                    <span>
                                      {
                                        matchTexts.participantStatuses[
                                          participant.status
                                        ]
                                      }
                                    </span>
                                  </div>
                                  {canConfirm ? (
                                    <div className="profile-open-match-request__actions">
                                      <button
                                        type="button"
                                        className="profile-open-match-request__action profile-open-match-request__action--confirm"
                                        onClick={() =>
                                          onReviewParticipant(
                                            match.id,
                                            participant.id,
                                            "confirmed",
                                          )
                                        }
                                        disabled={
                                          reviewingParticipantId ===
                                          participant.id
                                        }
                                        aria-label={
                                          matchTexts.approveParticipant
                                        }
                                        title={matchTexts.approveParticipant}
                                      >
                                        <span
                                          className="material-symbols-outlined"
                                          aria-hidden="true"
                                        >
                                          check
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        className="profile-open-match-request__action profile-open-match-request__action--decline"
                                        onClick={() =>
                                          onReviewParticipant(
                                            match.id,
                                            participant.id,
                                            "declined",
                                          )
                                        }
                                        disabled={
                                          reviewingParticipantId ===
                                          participant.id
                                        }
                                        aria-label={
                                          matchTexts.declineParticipant
                                        }
                                        title={matchTexts.declineParticipant}
                                      >
                                        <span
                                          className="material-symbols-outlined"
                                          aria-hidden="true"
                                        >
                                          close
                                        </span>
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="profile-open-match-request__actions">
                                      <button
                                        type="button"
                                        className="profile-open-match-request__action profile-open-match-request__action--decline"
                                        onClick={() =>
                                          onReviewParticipant(
                                            match.id,
                                            participant.id,
                                            "declined",
                                          )
                                        }
                                        disabled={
                                          reviewingParticipantId ===
                                          participant.id
                                        }
                                        aria-label={
                                          matchTexts.declineParticipant
                                        }
                                        title={matchTexts.declineParticipant}
                                      >
                                        <span
                                          className="material-symbols-outlined"
                                          aria-hidden="true"
                                        >
                                          close
                                        </span>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </ProfileAccordionSection>
  );
}
